/**
 * The typed error every API call rejects with.
 *
 * Callers switch on `code`, never on `message`. The message is human-readable,
 * gets reworded by product, and is translated into Dhivehi — a UI that
 * branches on it breaks the first time someone fixes a typo. The code list
 * below is copied from `api/openapi/_shared.yaml#/components/schemas/ErrorCode`
 * and adding a value there is backwards-compatible, which is why `ApiErrorCode`
 * keeps an open `(string & {})` arm: an unknown code from a newer server must
 * still be *representable*, not a type error that stops the app from
 * displaying it.
 */

export type KnownApiErrorCode =
  | 'validation_failed'
  | 'not_found'
  | 'conflict'
  | 'unauthenticated'
  | 'forbidden'
  | 'rate_limited'
  | 'idempotency_key_reused'
  | 'session_not_open'
  | 'document_finalized'
  | 'ledger_imbalance'
  | 'insufficient_balance'
  | 'rate_not_effective'
  | 'quota_exhausted'
  | 'ingest_conflict'
  | 'device_revoked'
  | 'key_revoked'
  | 'signature_invalid'
  | 'clock_skew'
  | 'replay_detected'
  | 'scope_missing'
  | 'otp_invalid'
  | 'otp_expired'
  | 'assistant_unavailable'
  | 'internal_error'
  | 'service_unavailable'

/** Client-side codes. These never come from the server; the wrapper raises them. */
export type ClientApiErrorCode = 'network_error' | 'timeout' | 'aborted' | 'malformed_response'

export type ApiErrorCode = KnownApiErrorCode | ClientApiErrorCode | (string & {})

/** One field-level validation failure, as the API's `Error.fields[]` entry. */
export interface ApiFieldError {
  field: string
  code: string
  message: string
}

export interface ApiErrorInit {
  code: ApiErrorCode
  message: string
  /** HTTP status, or 0 when the request never reached the server. */
  status: number
  fields?: ApiFieldError[]
  details?: Record<string, unknown>
  requestId?: string
  cause?: unknown
}

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number
  readonly fields: ApiFieldError[]
  readonly details: Record<string, unknown>
  readonly requestId: string | undefined

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause })
    this.name = 'ApiError'
    this.code = init.code
    this.status = init.status
    this.fields = init.fields ?? []
    this.details = init.details ?? {}
    this.requestId = init.requestId
    // Restores the prototype chain when the package is transpiled to ES5 by a
    // consumer's build; without it `instanceof ApiError` is false.
    Object.setPrototypeOf(this, ApiError.prototype)
  }

  /** True when the call never got an answer — offline, DNS, or a dropped link. */
  get isNetwork(): boolean {
    return this.code === 'network_error' || this.code === 'timeout'
  }

  /** True when the caller cancelled deliberately. Usually not worth surfacing. */
  get isAborted(): boolean {
    return this.code === 'aborted'
  }

  /** True when retrying the exact same request could plausibly succeed. */
  get isRetryable(): boolean {
    if (this.isAborted) return false
    if (this.isNetwork) return true
    return this.status === 429 || this.status === 502 || this.status === 503 || this.status === 504
  }

  /** Field errors keyed by field name, for binding onto form inputs. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const field of this.fields) out[field.field] = field.message
    return out
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

/** Narrowing helper: `if (hasCode(err, 'quota_exhausted')) …`. */
export function hasCode(value: unknown, ...codes: ApiErrorCode[]): value is ApiError {
  return isApiError(value) && codes.includes(value.code)
}
